import React, { useState } from 'react';
import { Phone, Mail, Award, BookOpen, Microscope, Users, GraduationCap, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { mockData } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    grade: '',
    curriculum: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/enquiries`, formData);
      console.log('Enquiry submitted:', response.data);
      toast.success('Thank you! Your inquiry has been submitted successfully.');
      setFormData({ name: '', email: '', phone: '', grade: '', curriculum: '', message: '' });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919205528717?text=Hello, I would like to inquire about Chemistry classes with Tejeswi.', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-2 rounded-lg">
                <Microscope className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-purple-900">Tejeswi</h1>
                <p className="text-xs text-purple-600">Excellence in Chemistry</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-700 hover:text-purple-700 transition-colors">About</a>
              <a href="#expertise" className="text-gray-700 hover:text-purple-700 transition-colors">Expertise</a>
              <a href="#results" className="text-gray-700 hover:text-purple-700 transition-colors">Results</a>
              <a href="#contact" className="text-gray-700 hover:text-purple-700 transition-colors">Contact</a>
            </nav>
            <Button onClick={handleWhatsAppClick} className="bg-green-600 hover:bg-green-700 text-white">
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 to-purple-600/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                  PhD in Chemistry • 22+ Years Experience
                </span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Building Strong
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-purple-900">
                  Scientific Foundations
                </span>
              </h2>
              <p className="text-xl text-gray-600">
                World-class Chemistry education for IGCSE (Cambridge) and CBSE curricula. 
                Producing 15+ World Toppers through concept-driven teaching and personalized mentoring.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-purple-700 hover:bg-purple-800 text-white px-8">
                  Enroll Now
                </Button>
                <Button onClick={handleWhatsAppClick}
                size="lg"
                variant="outline"
                className="border-purple-700 text-purple-700 hover:bg-purple-50 px-8">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Quick Inquiry
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 to-purple-600 rounded-3xl opacity-20 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1602052577122-f73b9710adba"
                alt="Modern Chemistry Laboratory"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover" />

            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-purple-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) =>
            <div key={index} className="text-center space-y-2">
                <div className="text-4xl lg:text-5xl font-bold">{stat.value}</div>
                <div className="text-purple-200">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">About Tejeswi</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A distinguished Chemistry educator combining deep academic expertise with proven teaching excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                  <GraduationCap className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Academic Excellence</h4>
                  <p className="text-gray-600">
                    Doctorate (PhD) in Chemistry with over 22 years of teaching and academic experience. 
                    Dedicated to building strong scientific foundations across IGCSE and CBSE curricula.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                  <Award className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Proven Results</h4>
                  <p className="text-gray-600">
                    15+ students achieved World Topper ranks in Chemistry - a rare distinction reflecting 
                    concept-driven teaching and personalized performance tracking.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                  <Microscope className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Practical Focus</h4>
                  <p className="text-gray-600">
                    Strong emphasis on laboratory skills, experimental accuracy, and conceptual linkage 
                    between theory and practical work.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1608037222022-62649819f8aa"
                alt="Students in Chemistry Lab"
                className="rounded-2xl shadow-xl w-full h-[450px] object-cover" />

              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg border-2 border-purple-200">
                <p className="text-2xl font-bold text-purple-700">22+</p>
                <p className="text-sm text-gray-600">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Academic Expertise</h3>
            <p className="text-xl text-gray-600">Comprehensive chemistry education across multiple curricula</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.expertise.map((item, index) =>
            <Card key={index} className="border-purple-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardHeader>
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-4 rounded-lg w-fit mb-4">
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-gray-900">{item.title}</CardTitle>
                  <CardDescription className="text-gray-600">{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {item.points.map((point, idx) =>
                  <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                  )}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Results That Speak Globally</h3>
            <p className="text-xl text-gray-600">15+ World Toppers in Chemistry - A testament to excellence</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <img
              src="https://images.unsplash.com/photo-1579154204601-01588f351e67"
              alt="Advanced Laboratory"
              className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />

            <div className="space-y-6">
              <h4 className="text-2xl font-bold text-gray-900">World-Class Performance</h4>
              <p className="text-gray-600 leading-relaxed">
                During the course of teaching, more than 15 students mentored by Tejeswi have achieved 
                World Topper ranks in Chemistry - a rare distinction that reflects exceptional teaching methodology.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {mockData.achievements.map((achievement, index) =>
                <div key={index} className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="bg-purple-600 p-1 rounded">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-semibold text-gray-900">{achievement.title}</span>
                    </div>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl p-8 lg:p-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center">Student Testimonials</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {mockData.testimonials.map((testimonial, index) =>
              <Card key={index} className="bg-white border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(5)].map((_, i) =>
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                    )}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-purple-600">{testimonial.achievement}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Teaching Methodology */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Teaching Philosophy</h3>
            <p className="text-2xl text-purple-700 font-semibold italic mb-4">
              "Strong concepts create confident students. Confident students create extraordinary results."
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1532094349884-543bc11b234d"
                alt="Chemistry Equipment"
                className="rounded-2xl shadow-xl w-full h-[450px] object-cover" />

            </div>
            <div>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {mockData.methodology.map((item, index) =>
                <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-purple-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                      {item.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {item.description}
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Learning */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-3xl lg:text-4xl font-bold text-gray-900">Practical-Focused Learning</h3>
              <p className="text-xl text-gray-600">
                Chemistry is not just theory—it is experimentation and application.
              </p>
              <div className="space-y-4">
                {mockData.practicalFocus.map((item, index) =>
                <div key={index} className="flex items-start space-x-4 bg-purple-50 p-4 rounded-lg">
                    <div className="bg-purple-600 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">{item.title}</h5>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/3735745/pexels-photo-3735745.jpeg"
                alt="Laboratory Experiments"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover" />

            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Start Your Chemistry Journey</h3>
            <p className="text-xl text-gray-600">Enroll now for world-class chemistry education</p>
          </div>
          <Card className="border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      required
                      className="border-purple-200 focus:border-purple-500" />

                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-purple-200 focus:border-purple-500" />

                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="border-purple-200 focus:border-purple-500" />

                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Grade *</label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">

                      <option value="">Select Grade</option>
                      <option value="9">Grade 9</option>
                      <option value="10">Grade 10</option>
                      <option value="11">Grade 11</option>
                      <option value="12">Grade 12</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Curriculum *</label>
                  <select
                    name="curriculum"
                    value={formData.curriculum}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">

                    <option value="">Select Curriculum</option>
                    <option value="IGCSE">IGCSE / Cambridge</option>
                    <option value="CBSE">CBSE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your learning goals..."
                    rows={4}
                    className="border-purple-200 focus:border-purple-500" />

                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button type="submit" size="lg" className="bg-purple-700 hover:bg-purple-800 text-white flex-1">
                    Submit Inquiry
                  </Button>
                  <Button
                    type="button"
                    onClick={handleWhatsAppClick}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white flex-1">

                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp Us
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-purple-900 to-purple-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-white p-2 rounded-lg">
                  <Microscope className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="text-xl font-bold">Tejeswi</h4>
                  <p className="text-purple-200 text-sm">Excellence in Chemistry</p>
                </div>
              </div>
              <p className="text-purple-200">
                PhD in Chemistry • 22+ Years Experience<br />
                15+ World Toppers
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-purple-200">
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#expertise" className="hover:text-white transition-colors">Expertise</a></li>
                <li><a href="#results" className="hover:text-white transition-colors">Results</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Contact</h5>
              <div className="space-y-3 text-purple-200">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>+91 9205528717
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>chemistry@tejeswi.com</span>
                </div>
                <Button onClick={handleWhatsAppClick}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white mt-4">

                  <MessageCircle className="h-4 w-4 mr-2" />
                  Connect on WhatsApp
                </Button>
              </div>
            </div>
          </div>
          <div className="border-t border-purple-700 mt-8 pt-8 text-center text-purple-200">
            <p>&copy; 2025 Tejeswi Chemistry Education. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;